import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import PageShell from "../dashboard/PageShell";

interface AddNewContractProps {}

interface ContractFormData {
  contractName: string;
  contractCode: string;
  contractType: string;
  implementingUnit: string;
  signingPartner: string;
  phone: string;
  idNumber: string;
  startDay: string;
  startMonth: string;
  startYear: string;
  endDay: string;
  endMonth: string;
  endYear: string;
  serviceType: string;
  technology: string;
  contractValue: string;
  notes: string;
  attachedFiles: File[];
}

const AddNewContract: React.FC<AddNewContractProps> = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<ContractFormData>({
    contractName: "",
    contractCode: "",
    contractType: "",
    implementingUnit: "",
    signingPartner: "",
    phone: "",
    idNumber: "",
    startDay: "",
    startMonth: "",
    startYear: "",
    endDay: "",
    endMonth: "",
    endYear: "",
    serviceType: "",
    technology: "",
    contractValue: "",
    notes: "",
    attachedFiles: [],
  });

  const handleInputChange = (
    field: keyof ContractFormData,
    value: string | File | File[] | null
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as never,
    }));
  };

  const handleAttachmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      handleInputChange("attachedFiles", [
        ...formData.attachedFiles,
        ...newFiles,
      ]);
    }
  };

  const handleCancel = () => {
    navigate("/contracts");
  };

  const handleSave = () => {
    console.log("Saving contract data:", formData);
    // TODO: Implement save functionality
    navigate("/contracts");
  };

  // Generate day, month, year options
  const days = Array.from({ length: 31 }, (_, i) => `${i + 1}`);
  const months = Array.from({ length: 12 }, (_, i) => `${i + 1}`);
  const years = Array.from({ length: 21 }, (_, i) => 2010 + i); // 2010-2030

  return (
    <PageShell title="Thêm mới Hợp đồng">
      <div className="space-y-6">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Thêm mới Hợp đồng
          </h1>
        </div>

        {/* Form Section */}
        <div className="bg-white rounded-lg p-6">
          <div className="space-y-8">
            {/* Row 1: Tên hợp đồng - Mã hợp đồng - Loại hình */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tên hợp đồng <span className="text-red-500">(*)</span>
                </label>
                <input
                  type="text"
                  value={formData.contractName}
                  onChange={(e) =>
                    handleInputChange("contractName", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập tên hợp đồng..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mã hợp đồng
                </label>
                <input
                  type="text"
                  value={formData.contractCode}
                  onChange={(e) =>
                    handleInputChange("contractCode", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập mã hợp đồng..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Loại hình
                </label>
                <select
                  value={formData.contractType}
                  onChange={(e) =>
                    handleInputChange("contractType", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn loại -</option>
                  <option>Hợp đồng dịch vụ</option>
                  <option>Hợp đồng nghiên cứu</option>
                  <option>Hợp đồng tư vấn</option>
                </select>
              </div>
            </div>

            {/* Ngày ký hợp đồng - 3 columns in one row */}
            <div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ngày
                  </label>
                  <select
                    value={formData.startDay}
                    onChange={(e) =>
                      handleInputChange("startDay", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn ngày -</option>
                    {days.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Tháng
                  </label>
                  <select
                    value={formData.startMonth}
                    onChange={(e) =>
                      handleInputChange("startMonth", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option>- Chọn tháng -</option>
                    {months.map((month) => (
                      <option key={month} value={month}>
                        {month}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Năm ký hợp đồng <span className="text-red-500">(*)</span>
                  </label>
                  <input
                    type="number"
                    min="1900"
                    max="2100"
                    value={formData.startYear}
                    onChange={(e) =>
                      handleInputChange("startYear", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập năm..."
                  />
                </div>
              </div>
            </div>

            {/* Thời gian thực hiện */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4">
                Thời gian thực hiện
              </label>

              {/* Bắt đầu - Kết thúc cùng 1 hàng */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bắt đầu
                  </label>
                  <input
                    type="date"
                    value={`${
                      formData.startYear
                    }-${formData.startMonth?.padStart(
                      2,
                      "0"
                    )}-${formData.startDay?.padStart(2, "0")}`}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      handleInputChange("startDay", date.getDate().toString());
                      handleInputChange(
                        "startMonth",
                        (date.getMonth() + 1).toString()
                      );
                      handleInputChange(
                        "startYear",
                        date.getFullYear().toString()
                      );
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Kết thúc
                  </label>
                  <input
                    type="date"
                    value={`${formData.endYear}-${formData.endMonth?.padStart(
                      2,
                      "0"
                    )}-${formData.endDay?.padStart(2, "0")}`}
                    onChange={(e) => {
                      const date = new Date(e.target.value);
                      handleInputChange("endDay", date.getDate().toString());
                      handleInputChange(
                        "endMonth",
                        (date.getMonth() + 1).toString()
                      );
                      handleInputChange(
                        "endYear",
                        date.getFullYear().toString()
                      );
                    }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Row: Đơn vị phòng ban - Cá nhân đứng tên - Điện thoại - Số CMT/Thẻ căn cước - Chức vụ */}
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4  rounded-md p-4 bg-gray-50">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đơn vị phòng ban <span className="text-red-500">(*)</span>
                </label>
                <select
                  value={formData.implementingUnit}
                  onChange={(e) =>
                    handleInputChange("implementingUnit", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn đơn vị phòng ban -</option>
                  <option>Phòng Khoa học</option>
                  <option>Khoa CNTT</option>
                  <option>Khoa Cơ khí</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cá nhân đứng tên
                </label>
                <select
                  value={formData.signingPartner}
                  onChange={(e) =>
                    handleInputChange("signingPartner", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn cá nhân -</option>
                  <option>Nguyễn Văn A</option>
                  <option>Trần Thị B</option>
                  <option>Lê Văn C</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Điện thoại
                </label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange("phone", e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập số điện thoại..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Số CMT/ Thẻ căn cước
                </label>
                <input
                  type="text"
                  value={formData.idNumber}
                  onChange={(e) =>
                    handleInputChange("idNumber", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập số CMT/CCCD..."
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Chức vụ
                </label>
                <select
                  value={formData.serviceType}
                  onChange={(e) =>
                    handleInputChange("serviceType", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn chức vụ -</option>
                  <option>Trưởng dự án</option>
                  <option>Phó dự án</option>
                  <option>Thành viên</option>
                </select>
              </div>
            </div>

            {/* Công nghệ chuyển giao - Giá trị hợp đồng - Đối tác cùng hàng */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Công nghệ chuyển giao
                </label>
                <select
                  value={formData.technology}
                  onChange={(e) =>
                    handleInputChange("technology", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option>- Chọn loại -</option>
                  <option>Công nghệ thông tin</option>
                  <option>Công nghệ sinh học</option>
                  <option>Công nghệ cơ khí</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Giá trị hợp đồng
                </label>
                <div className="flex">
                  <input
                    type="text"
                    value={formData.contractValue}
                    onChange={(e) =>
                      handleInputChange("contractValue", e.target.value)
                    }
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-l-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Nhập giá trị hợp đồng..."
                  />
                  <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-md text-sm bg-gray-50 text-gray-600">
                    (VND)
                  </span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Đối tác
                </label>
                <input
                  type="text"
                  value={formData.signingPartner}
                  onChange={(e) =>
                    handleInputChange("signingPartner", e.target.value)
                  }
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Nhập thông tin đối tác..."
                />
              </div>
            </div>

            {/* Ghi chú */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ghi chú
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                className="w-full h-24 px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                placeholder="Nhập ghi chú..."
              />
            </div>

            {/* File đính kèm */}
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  File đính kèm
                </label>

                {/* Custom File Input */}
                <div className="flex items-center space-x-4">
                  <input
                    id="contract-file-upload"
                    type="file"
                    multiple
                    onChange={handleAttachmentChange}
                    className="hidden"
                  />
                  <label
                    htmlFor="contract-file-upload"
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 cursor-pointer transition-colors"
                  >
                    Choose Files
                  </label>
                  <span className="text-sm text-gray-500">
                    {formData.attachedFiles.length === 0
                      ? "No file chosen"
                      : `${formData.attachedFiles.length} file(s) chosen`}
                  </span>
                </div>

                {/* File List */}
                {formData.attachedFiles.length > 0 && (
                  <div className="mt-4">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">
                      Files:
                    </h4>
                    <div
                      className={`space-y-2 ${
                        formData.attachedFiles.length > 3
                          ? "max-h-48 overflow-y-auto pr-2"
                          : ""
                      }`}
                    >
                      {formData.attachedFiles.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-2 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-2 min-w-0 flex-1">
                            <svg
                              className="w-4 h-4 text-gray-400 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {file.name}
                              </div>
                              <div className="text-xs text-gray-500">
                                {(file.size / 1024).toFixed(1)} KB
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              const updatedFiles =
                                formData.attachedFiles.filter(
                                  (_, i) => i !== index
                                );
                              handleInputChange("attachedFiles", updatedFiles);
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 rounded transition-colors flex-shrink-0"
                            title="Xóa file"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md text-sm font-medium hover:bg-gray-400 transition-colors flex items-center space-x-2"
          >
            <span>Hủy</span>
          </button>
          <button
            onClick={handleSave}
            className="bg-green-600 text-white px-6 py-2 rounded-md text-sm font-medium hover:bg-green-700 transition-colors flex items-center space-x-2"
          >
            <span>Lưu dữ liệu</span>
          </button>
        </div>
      </div>
    </PageShell>
  );
};

export default AddNewContract;
